import { useI18n } from '../../i18n';
import { polar, ringSegment } from './geometry';

/**
 * A ring segment showing how the two grids lock together: one rashi's 30°
 * spans exactly 2¼ nakshatras of 13°20′ each — Ashwini, Bharani, and the
 * first quarter of Krittika — with Ashwini marked as the start of the count.
 */

// Band geometry: a large circle whose centre sits below the viewBox, so the
// visible slice reads as a gently curved band.
const CX = 180;
const CY = 336;
const R_OUT = 292;
const R_IN = 240;

// The 30° of one rashi is DRAWN as 64° of arc; each segment keeps its true
// proportion of the whole (13°20′ = 13.333…°).
const DRAWN = 64;
const A0 = -DRAWN / 2;
const seg = (deg: number): number => (deg / 30) * DRAWN;

const ASHWINI_END = A0 + seg(13.334);
const BHARANI_END = ASHWINI_END + seg(13.334);

export function NakshatraBand() {
  const { t } = useI18n();
  const mid = (R_OUT + R_IN) / 2;
  const [ax, ay] = polar(CX, CY, mid, A0 + seg(13.334) / 2);
  const [bx, by] = polar(CX, CY, mid, ASHWINI_END + seg(13.334) / 2);
  const [startX, startY] = polar(CX, CY, R_OUT + 10, A0);
  return (
    <svg
      viewBox="0 0 360 210"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.nakshatraBand.alt')}
    >
      {/* The rashi: one 30° slice, named as a whole above the band. */}
      <text
        x={CX}
        y={26}
        textAnchor="middle"
        fontSize="12"
        fontWeight="700"
        fill="var(--mode-ink)"
      >
        Mesha — 30°
      </text>

      <path
        d={ringSegment(CX, CY, R_OUT, R_IN, A0, ASHWINI_END)}
        fill="var(--mode-soft)"
        stroke="var(--border-strong)"
        strokeWidth="1"
      />
      <path
        d={ringSegment(CX, CY, R_OUT, R_IN, ASHWINI_END, BHARANI_END)}
        fill="transparent"
        stroke="var(--border-strong)"
        strokeWidth="1"
      />
      <path
        d={ringSegment(CX, CY, R_OUT, R_IN, BHARANI_END, A0 + DRAWN)}
        fill="var(--gold-soft)"
        stroke="var(--border-strong)"
        strokeWidth="1"
      />

      <text x={ax} y={ay + 3} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--text)">
        Ashwini
      </text>
      <text x={bx} y={by + 3} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--text)">
        Bharani
      </text>

      {/* 13°20′ under the two full segments; the sliver is ¼ of Krittika. */}
      <text x={ax} y={ay + 42} textAnchor="middle" fontSize="8.5" fill="var(--text-faint)">
        13°20′
      </text>
      <text x={bx} y={by + 42} textAnchor="middle" fontSize="8.5" fill="var(--text-faint)">
        13°20′
      </text>
      <text
        x={polar(CX, CY, R_OUT + 10, A0 + DRAWN)[0] - 2}
        y={polar(CX, CY, R_OUT + 10, A0 + DRAWN)[1] - 4}
        textAnchor="end"
        fontSize="8.5"
        fill="var(--gold-ink)"
      >
        Krittika · ¼
      </text>

      {/* Where the whole 27-fold count begins. */}
      <circle cx={startX} cy={startY} r="3" fill="var(--mode)" />
      <text x={startX + 2} y={startY - 10} fontSize="9" fill="var(--mode-ink)">
        {t('learn.fig.nakshatraBand.start')}
      </text>

      <text x={CX} y={200} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.nakshatraBand.note')}
      </text>
    </svg>
  );
}
